import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "@/constants/categories";
import { formatPrice } from "@/data/products";
import { useAdminProductList, useInvalidateProducts } from "@/hooks/useProducts";
import { deleteProduct } from "@/lib/api";

const ADMIN_PAGE_SIZE = 100;

export const AdminProductsPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = search.trim();
  const { data, isLoading, isError, refetch } = useAdminProductList({
    page,
    limit: ADMIN_PAGE_SIZE,
    category: category || undefined,
    q: query || undefined,
    sort: "name",
  });
  const invalidateProducts = useInvalidateProducts();

  const products = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  useEffect(() => {
    setPage(1);
  }, [search, category]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Delete product “${id}”?`)) return;
    setDeletingId(id);
    try {
      await deleteProduct(id);
      await invalidateProducts(id);
      await refetch();
    } catch {
      setActionError("Failed to delete product");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          <p className="text-sm text-vg-gray mt-1">
            {isLoading ? "Loading…" : `${products.length} shown on page ${page} of ${totalPages} (${total} total)`}
          </p>
        </div>
        <Link
          to="/admin/products/new"
          className="inline-flex items-center justify-center h-10 px-4 bg-vg-navy text-white text-sm font-medium hover:bg-vg-cyan"
          data-testid="admin-new-product"
        >
          Add product
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="search"
          placeholder="Search name, brand, id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
          data-testid="admin-product-search"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black sm:w-56"
          data-testid="admin-product-category-filter"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {(isError || actionError) && (
        <p className="text-sm text-red-600 mb-4">{actionError ?? "Failed to load products"}</p>
      )}

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="border-b border-black/10 bg-[#f8fafc]">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Brand</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Price</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-vg-gray">
                  Loading products…
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-vg-gray">
                  No products found
                </td>
              </tr>
            ) : (
              products.map((p) => (
                <tr key={p.id} className="border-b border-black/5 last:border-0" data-testid={`admin-product-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-vg-gray font-mono">{p.id}</div>
                  </td>
                  <td className="px-4 py-3">{p.brand}</td>
                  <td className="px-4 py-3">{p.categorySlug}</td>
                  <td className="px-4 py-3">{formatPrice(p.price)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link
                      to={`/admin/products/${encodeURIComponent(p.id)}/edit`}
                      className="text-vg-ink underline-offset-2 hover:underline mr-3"
                      data-testid={`admin-product-edit-${p.id}`}
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(p.id)}
                      disabled={deletingId === p.id}
                      className="text-red-600 hover:underline disabled:opacity-50"
                    >
                      {deletingId === p.id ? "Deleting…" : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1 || isLoading}
            className="px-3 py-2 border border-black/10 text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-vg-gray">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page === totalPages || isLoading}
            className="px-3 py-2 border border-black/10 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;
